export const SAVE_STATES = Object.freeze({
  SAVING: 'Saving...',
  SAVED_JUST_NOW: 'Saved just now',
  OFFLINE: 'Offline - saved on this device',
  UNSAVED: 'We could not save yet - your work is still on this device',
});

const DEFAULT_RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];

function clone(value) {
  return value === null || value === undefined
    ? value
    : structuredClone(value);
}

function savedAtLabel(savedAt) {
  return `Saved at ${String(savedAt).slice(11, 16)}`;
}

function currentSectionValue(currentSection) {
  return typeof currentSection === 'function' ? currentSection() : currentSection;
}

function createOperation({ currentSection, pendingUpdates, randomUUID, schemaVersion }) {
  return {
    operationId: randomUUID(),
    schemaVersion,
    currentSection: currentSectionValue(currentSection) || 'introduction',
    updates: clone(pendingUpdates),
  };
}

export function createAutosave({
  savePatch,
  queue,
  onStatus = () => {},
  onInvalidSession = () => {},
  onSubmitted = () => {},
  currentSection = () => 'introduction',
  schemaVersion = 1,
  debounceMs = 800,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
  randomUUID = () => globalThis.crypto.randomUUID(),
  setTimer = globalThis.setTimeout.bind(globalThis),
  clearTimer = globalThis.clearTimeout.bind(globalThis),
} = {}) {
  let pendingUpdates = {};
  let debounceTimer = null;
  let retryTimer = null;
  let retryIndex = 0;
  let flushPromise = null;
  let stopped = false;

  function hasPendingUpdates() {
    return Object.keys(pendingUpdates).length > 0;
  }

  function status(label) {
    onStatus(label);
  }

  function clearDebounce() {
    if (debounceTimer !== null) {
      clearTimer(debounceTimer);
      debounceTimer = null;
    }
  }

  function clearRetry() {
    if (retryTimer !== null) {
      clearTimer(retryTimer);
      retryTimer = null;
    }
  }

  function scheduleRetry() {
    if (stopped || retryTimer !== null || retryDelaysMs.length === 0) return;
    const delay = retryDelaysMs[Math.min(retryIndex, retryDelaysMs.length - 1)];
    retryIndex += 1;
    retryTimer = setTimer(() => {
      retryTimer = null;
      void flushQueue();
    }, delay);
  }

  async function queuePendingUpdates() {
    if (!hasPendingUpdates()) return;
    const operation = createOperation({
      currentSection,
      pendingUpdates,
      randomUUID,
      schemaVersion,
    });
    pendingUpdates = {};
    await queue.enqueue(operation);
  }

  async function failureState() {
    return (await queue.isPersistent?.()) === false ? SAVE_STATES.UNSAVED : SAVE_STATES.OFFLINE;
  }

  async function handleFailure() {
    await queuePendingUpdates();
    status(await failureState());
    scheduleRetry();
    return { state: 'queued' };
  }

  async function handleResponse(operation, response) {
    if (response?.status === 200 && response.data?.state === 'saved') {
      await queue.acknowledge(operation.operationId);
      retryIndex = 0;
      return { state: 'saved' };
    }

    if (response?.data?.state === 'submitted' || response?.status === 409) {
      clearDebounce();
      clearRetry();
      pendingUpdates = {};
      await queue.clear();
      onSubmitted(response.data);
      return { state: 'submitted' };
    }

    if (response?.status === 401) {
      await queuePendingUpdates();
      status(await failureState());
      onInvalidSession(response.data);
      return { state: 'invalid_session' };
    }

    if (response?.status === 400) {
      // A permanent rejection: this operation can never be saved as-is (too large or
      // malformed). Drop it so it cannot wedge the head of the queue and silently block
      // every later save, and surface the failure instead of retrying it forever.
      await queue.acknowledge(operation.operationId);
      status(SAVE_STATES.UNSAVED);
      return { state: 'rejected' };
    }

    return handleFailure();
  }

  async function flushQueue() {
    if (stopped) return { state: 'stopped' };
    if (flushPromise) return flushPromise;

    flushPromise = (async () => {
      let savedAny = false;
      let rejectedAny = false;

      while (!stopped) {
        await queuePendingUpdates();
        const operations = await queue.pending();
        if (operations.length === 0) {
          if (rejectedAny) return { state: 'rejected' };
          if (savedAny) status(SAVE_STATES.SAVED_JUST_NOW);
          return savedAny ? { state: 'saved' } : { state: 'idle' };
        }

        status(SAVE_STATES.SAVING);
        for (const operation of operations) {
          try {
            const result = await handleResponse(operation, await savePatch(operation));
            if (result.state === 'saved') {
              savedAny = true;
              continue;
            }
            // A rejected operation has already been dropped from the queue; keep
            // draining so it cannot block the operations queued behind it.
            if (result.state === 'rejected') {
              rejectedAny = true;
              continue;
            }
            return result;
          } catch {
            return handleFailure();
          }
        }
      }

      return { state: 'stopped' };
    })().finally(() => {
      flushPromise = null;
    });

    return flushPromise;
  }

  function scheduleSave() {
    clearDebounce();
    debounceTimer = setTimer(() => {
      debounceTimer = null;
      void flushQueue();
    }, debounceMs);
  }

  return {
    change(questionId, value) {
      pendingUpdates = { ...pendingUpdates, [questionId]: clone(value) };
      scheduleSave();
    },
    async flush() {
      clearDebounce();
      return flushQueue();
    },
    async reconnect() {
      clearRetry();
      retryIndex = 0;
      return this.flush();
    },
    restoreStatus(draft = {}) {
      status(draft.updatedAt ? savedAtLabel(draft.updatedAt) : SAVE_STATES.SAVED_JUST_NOW);
    },
    async clear() {
      clearDebounce();
      clearRetry();
      pendingUpdates = {};
      await queue.clear();
    },
    destroy() {
      stopped = true;
      clearDebounce();
      clearRetry();
    },
  };
}
